import { useState } from 'react'
import { useDebounce } from 'react-use'

import { InputSearchbox } from '@/components/common/Input'
import { type CardFilterSearchType, type CardFilterType } from '@/defs/cardFilters'
import { galleryActions } from '@/store/gallery'
import { useReduxDispatch, useReduxSelector } from '@/store/hooks'
import { cn } from '@/utils/commonUtil'

import { useGalleryGroups } from '../../GalleryGroupsProvider/GalleryGroupsProvider'
import { useGalleryPreload } from '../../GalleryPreloadProvider/GalleryPreloadProvider'

import styles from './GalleryControlsSearch.module.scss'

const getCardName = (cardFilters?: CardFilterType[]) =>
  cardFilters?.find((cardFilter): cardFilter is CardFilterSearchType => cardFilter.slug === 'cardName')?.search || ''

type Props = {
  variant?: 'desktop'
}

export const GalleryControlsSearch = ({ variant }: Props) => {
  const { paginationTotal = 0 } = useGalleryGroups()
  const { startPreload } = useGalleryPreload()
  const cardFilters = useReduxSelector((s) => s.gallery.cardFilters)
  const dispatch = useReduxDispatch()
  const cardName = getCardName(cardFilters)
  const [input, setInput] = useState(cardName)
  const [prevCardName, setPrevCardName] = useState(cardName)
  if (cardName !== prevCardName) {
    setPrevCardName(cardName)
    setInput(cardName)
  }

  useDebounce(
    () => {
      if (input === cardName) return

      const cardFilter = {
        search: input,
        slug: 'cardName' as const,
      }
      const cardFiltersNew = [cardFilter, ...(cardFilters || []).filter((f) => f.slug !== 'cardName')]
      dispatch(galleryActions.setCardFilters(cardFiltersNew))
    },
    500,
    [input],
  )

  if (paginationTotal > 1) return null

  return (
    <div className={cn(styles.container, { styles, variant })}>
      <InputSearchbox
        aria-label='Search card names'
        onChange={setInput}
        onClear={() => setInput('')}
        onFocus={() => startPreload()}
        placeholder='Search card names...'
        size='sm'
        value={input}
      />
    </div>
  )
}
